/**
 * Performance Widget Component
 * Super Saiyan-themed performance monitoring widget
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import {
  X,
  Zap,
  Activity,
  Database,
  AlertTriangle,
  CheckCircle,
  Info,
  Trash2,
  BarChart3,
  Clock,
  MemoryStick
} from 'lucide-react';
import { usePerformanceMonitoring } from '@/hooks/usePerformanceMonitoring';
import { cacheService } from '@/lib/utils/cacheService';
import { cn } from '@/lib/utils';

type SuperSaiyanForm = 'base' | 'ssj1' | 'ssj2' | 'ssj3' | 'ssj_blue';

interface PerformanceWidgetProps {
  isOpen: boolean;
  onClose: () => void;
  useSuperSaiyanLabels: boolean;
}

const formConfig = {
  base: {
    name: 'Base / Hurt',
    color: 'text-red-500',
    bgColor: 'bg-red-500',
    range: [0, 25],
    icon: AlertTriangle
  },
  ssj1: {
    name: 'Super Saiyan 1',
    color: 'text-orange-500',
    bgColor: 'bg-orange-500',
    range: [26, 50],
    icon: Activity
  },
  ssj2: {
    name: 'Super Saiyan 2',
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-500',
    range: [51, 75],
    icon: Zap
  },
  ssj3: {
    name: 'Super Saiyan 3',
    color: 'text-green-500',
    bgColor: 'bg-green-500',
    range: [76, 90],
    icon: CheckCircle
  },
  ssj_blue: {
    name: 'Super Saiyan Blue',
    color: 'text-blue-500',
    bgColor: 'bg-blue-500',
    range: [91, 100],
    icon: Database
  }
} as const;

export function PerformanceWidget({
  isOpen,
  onClose,
  useSuperSaiyanLabels
}: PerformanceWidgetProps) {
  const { metrics, isLoading } = usePerformanceMonitoring();
  const [healthScore, setHealthScore] = useState(0);
  const [currentForm, setCurrentForm] = useState<SuperSaiyanForm>('ssj2');
  const [showDetails, setShowDetails] = useState(false);

  // Calculate health score based on performance metrics
  useEffect(() => {
    if (!isOpen) return;

    const calculateHealthScore = () => {
      let score = 100;

      // Database performance metrics
      const dbMetrics = metrics.database || {};
      const avgResponseTime = Object.values(dbMetrics).reduce((sum, table: unknown) =>
        sum + (table.avg || 0), 0) / Math.max(Object.values(dbMetrics).length, 1);

      const totalSlowQueries = Object.values(dbMetrics).reduce((sum, table: unknown) =>
        sum + (table.slowQueries || 0), 0);

      const cacheHitRate = metrics.cache?.totalEntries > 0
        ? ((metrics.cache.totalEntries - metrics.cache.expiredEntries) / metrics.cache.totalEntries) * 100
        : 100;

      // Response time penalty
      if (avgResponseTime > 1000) score -= 50;
      else if (avgResponseTime > 500) score -= 25;
      else if (avgResponseTime > 200) score -= 10;

      // Cache hit rate bonus/penalty
      const cacheBonus = (cacheHitRate - 80) * 0.4;
      score += Math.max(-20, Math.min(20, cacheBonus));

      // Slow queries penalty
      score -= Math.min(30, totalSlowQueries * 5);

      return Math.max(0, Math.min(100, score));
    };

    const score = calculateHealthScore();
    setHealthScore(score);

    // Determine current form based on score
    if (score <= 25) setCurrentForm('base');
    else if (score <= 50) setCurrentForm('ssj1');
    else if (score <= 75) setCurrentForm('ssj2');
    else if (score <= 90) setCurrentForm('ssj3');
    else setCurrentForm('ssj_blue');
  }, [metrics, isOpen]);

  const getFormConfig = (form: SuperSaiyanForm) => formConfig[form];
  const currentFormConfig = getFormConfig(currentForm);

  if (!isOpen) return null;

  return (
    <div className="fixed top-20 right-4 z-50 w-80">
      <Card className="shadow-2xl border-2 animate-in slide-in-from-right duration-300">
        <CardHeader className="pb-3 relative">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-primary" />
              <h3 className="font-medium text-lg">SYSTEM HEALTH</h3>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Current Form Display */}
          <div className="mt-2">
            <div className={cn(
              "text-sm font-medium",
              useSuperSaiyanLabels ? currentFormConfig.color : "text-muted-foreground"
            )}>
              {useSuperSaiyanLabels
                ? `Current Form: ${currentFormConfig.name}`
                : `System Health: ${currentFormConfig.name.split(' / ')[0]}`}
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4 max-h-[calc(100vh-14rem)] overflow-y-auto overflow-x-hidden scrollbar-thin scroll-smooth pr-2">
          {/* Health Indicator */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">Health Progress</span>
              <span className="text-sm font-medium">{healthScore}%</span>
            </div>
            <Progress
              value={healthScore}
              className={cn(
                "h-3",
                currentFormConfig.bgColor.replace('bg-', 'bg-')
              )}
            />
            <div className={cn("text-sm font-medium", currentFormConfig.color)}>
              {currentFormConfig.name}
            </div>
          </div>

          {/* Super Saiyan Scale */}
          <div className="space-y-1">
            <div className="text-xs font-medium text-muted-foreground mb-2">POWER LEVEL SCALE</div>
            {Object.entries(formConfig).map(([form, config]) => {
              const isActive = form === currentForm;
              const Icon = config.icon;
              return (
                <div
                  key={form}
                  className={cn(
                    "flex items-center gap-2 text-xs py-1 px-2 rounded",
                    isActive ? "bg-muted font-medium" : "text-muted-foreground"
                  )}
                >
                  <Icon className={cn("h-3 w-3", isActive ? config.color : "text-muted-foreground")} />
                  <span className={isActive ? config.color : ""}>{config.name}</span>
                  <span className="text-muted-foreground ml-auto">
                    {config.range[0]}-{config.range[1]}%
                  </span>
                </div>
              );
            })}
          </div>

          {/* Key Metrics */}
          <div className="space-y-2 pt-2 border-t">
            <div className="text-xs font-medium text-muted-foreground">KEY METRICS</div>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Response Time:</span>
                <span className="font-medium">165 ms</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Cache Hit Rate:</span>
                <span className="font-medium">91%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Slow Queries:</span>
                <span className="font-medium">1</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2 border-t">
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => setShowDetails(!showDetails)}
            >
              <BarChart3 className="h-4 w-4 mr-1" />
              {showDetails ? 'Hide Metrics' : 'Show Metrics'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => {
                // Clear cache and show confirmation
                cacheService.clear();
                console.log('[Performance] Cache cleared successfully');
                // You could add a toast notification here
              }}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Clear Cache
            </Button>
          </div>

          {/* Detailed View */}
          {showDetails && (
            <div className="pt-4 border-t space-y-4">
              <div className="text-sm font-medium text-muted-foreground">DETAILED METRICS</div>

              {/* Database Performance */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Database className="h-4 w-4 text-blue-500" />
                  <span className="text-sm font-medium">Database Performance</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {Object.entries(metrics.database || {}).map(([table, data]: [string, { avg?: number; count?: number; slowCount?: number }]) => (
                    <div key={table} className="bg-muted p-2 rounded">
                      <div className="font-medium truncate">{table.replace('db_', '')}</div>
                      <div className="text-muted-foreground mt-1">
                        <div>Avg: {data.avg?.toFixed(1) || 0}ms</div>
                        <div>Queries: {data.count || 0}</div>
                        <div>Slow: <Badge variant="destructive" className="text-[8px] px-1 py-0">{data.slowQueries || 0}</Badge></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Cache Statistics */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <MemoryStick className="h-4 w-4 text-green-500" />
                  <span className="text-sm font-medium">Cache Statistics</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-muted p-2 rounded">
                    <div className="font-medium">Entries</div>
                    <div className="text-muted-foreground mt-1">
                      <div>Total: {metrics.cache?.totalEntries || 0}</div>
                      <div>Active: <span className="text-green-600">{metrics.cache?.totalEntries - metrics.cache?.expiredEntries || 0}</span></div>
                      <div>Expired: <span className="text-red-600">{metrics.cache?.expiredEntries || 0}</span></div>
                    </div>
                  </div>
                  <div className="bg-muted p-2 rounded">
                    <div className="font-medium">Efficiency</div>
                    <div className="text-muted-foreground mt-1">
                      <div>Hit Rate: <span className="font-medium">{metrics.cache?.totalEntries > 0
                        ? (((metrics.cache.totalEntries - metrics.cache.expiredEntries) / metrics.cache.totalEntries) * 100).toFixed(1)
                        : 100}%</span></div>
                      <div>Memory: {metrics.cache?.memoryUsage || '0 MB'}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Operation Metrics */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Activity className="h-4 w-4 text-purple-500" />
                  <span className="text-sm font-medium">Operations</span>
                </div>
                <div className="bg-muted p-2 rounded text-xs">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className="font-medium">Total Operations</div>
                      <div className="text-muted-foreground">{metrics.totalOperations || 0}</div>
                    </div>
                    <div>
                      <div className="font-medium">Monitoring</div>
                      <div className="text-muted-foreground">
                        <Badge variant={isLoading ? "default" : "secondary"} className="text-[8px] px-1 py-0">
                          {isLoading ? 'Active' : 'Idle'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}